import { useMemo } from "react";
import { ResponsiveContainer, AreaChart, Area, Tooltip, XAxis, YAxis } from "recharts";
import type { HistoryPoint } from "@/hooks/useLiveMarkets";

interface MiniSparklineProps {
  data: HistoryPoint[];
  positive: boolean;
  height?: number;
  showTooltip?: boolean;
}

const formatDate = (ts: number) => {
  if (!ts) return "";
  const d = new Date(ts * 1000);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const SparkTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.[0]) return null;
  const { t, v } = payload[0].payload;
  return (
    <div className="rounded-md bg-popover border border-border px-2 py-1 shadow-md text-[10px] pointer-events-none z-50">
      <span className="font-bold text-foreground">{v}%</span>
      {t > 0 && <span className="text-muted-foreground ml-1.5">{formatDate(t)}</span>}
    </div>
  );
};

/**
 * If history has fewer than minPoints, interpolate to fill it out
 * so the sparkline looks richer and the tooltip has more hover targets.
 */
function ensureMinPoints(data: HistoryPoint[], minPoints: number): HistoryPoint[] {
  if (data.length >= minPoints) return data;
  if (data.length === 0) return data;
  if (data.length === 1) {
    // Duplicate with slight time spread
    const pt = data[0];
    const spread = 86400; // 1 day
    const result: HistoryPoint[] = [];
    for (let i = 0; i < minPoints; i++) {
      result.push({ t: pt.t - (minPoints - 1 - i) * spread, v: pt.v });
    }
    return result;
  }

  // Linear interpolation between existing points
  const result: HistoryPoint[] = [];
  const totalSegments = minPoints - 1;
  for (let i = 0; i <= totalSegments; i++) {
    const frac = i / totalSegments;
    const srcIdx = frac * (data.length - 1);
    const lo = Math.floor(srcIdx);
    const hi = Math.min(lo + 1, data.length - 1);
    const alpha = srcIdx - lo;
    result.push({
      t: Math.round(data[lo].t + (data[hi].t - data[lo].t) * alpha),
      v: Math.round(data[lo].v + (data[hi].v - data[lo].v) * alpha),
    });
  }
  return result;
}

const MiniSparkline = ({ data, positive, height = 32, showTooltip = false }: MiniSparklineProps) => {
  const chartData = useMemo(() => {
    const normalized = data.map((pt, i) =>
      typeof pt === "number" ? { i, v: pt, t: 0 } : { i, v: pt.v, t: pt.t },
    );
    // Ensure at least 20 points for smooth hover + visual
    const enriched = ensureMinPoints(
      normalized.map((d) => ({ t: d.t, v: d.v })),
      20,
    );
    return enriched.map((pt, i) => ({ i, ...pt }));
  }, [data]);

  const color = positive ? "hsl(142, 71%, 45%)" : "hsl(0, 72%, 51%)";
  const gradientId = useMemo(
    () => `spark-${positive ? "up" : "down"}-${Math.random().toString(36).slice(2, 6)}`,
    [positive],
  );

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={chartData} margin={{ top: 2, right: 2, left: 2, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.3} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        {/* Hidden XAxis to enable smooth cursor tracking across the full width */}
        <XAxis dataKey="i" hide />
        <YAxis hide domain={["dataMin - 1", "dataMax + 1"]} />
        {showTooltip && (
          <Tooltip
            content={<SparkTooltip />}
            cursor={{ stroke: "hsl(var(--muted-foreground))", strokeWidth: 1, strokeDasharray: "3 3" }}
            isAnimationActive={false}
          />
        )}
        <Area
          type="monotone"
          dataKey="v"
          stroke={color}
          strokeWidth={1.5}
          fill={`url(#${gradientId})`}
          dot={false}
          activeDot={showTooltip ? { r: 3, stroke: color, fill: "hsl(var(--background))", strokeWidth: 2 } : false}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default MiniSparkline;
